import React, {useImperativeHandle, forwardRef, useMemo, useRef, useState, useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import styled from 'styled-components';
import {canvasFormBannerTypes} from '../../../../../../../util/uiconstants';
import {setIsModalOpen} from '../../../../../../../actions/setismodalopen';
import {getEntityBodyComponent, getEntityModalConfiguration} from './ViewEntityConfig';
import CanvasBanner from '../../../../../CanvasForm/Banner/CanvasBanner';
import Modal from '../../../../../Modal/Modal';
import LoadingIndicator from '../../../../../LoadingIndicator/LoadingIndicator';

export const ViewEntityContainer = forwardRef((props, ref) => {
	const {entityId, engagementId, documentEngagementId, documentId, entityUId, children, isAresView} = props;

	const isLoading = useSelector((store) => store.isLoading);

	const disableEditFeature = useMemo(() => engagementId !== documentEngagementId, [engagementId, documentEngagementId]);
	const component = getEntityBodyComponent(entityId);
	const configurations = getEntityModalConfiguration(entityId)({...props});

	const [showModal, setShowModal] = useState(false);

	const dispatch = useDispatch();

	const componentRef = useRef();

	useImperativeHandle(ref, () => {
		return {
			toggleModal() {
				setShowModal(!showModal);
			}
		};
	});

	const handleOnHide = () => {
		try {
			configurations.onModalHide && configurations.onModalHide();
		} finally {
			if (componentRef.current?.onHide) {
				componentRef.current.onHide();
			}
			setShowModal(false);
		}
	};

	const onCreateUpdate = () => {
		if (!entityUId && componentRef.current?.createEntity) {
			const promise = componentRef.current.createEntity();
			promise?.then(() => handleOnHide());
		}

		if (entityUId && componentRef.current?.updateEntity) {
			const promise = componentRef.current.updateEntity();
			promise?.then(() => handleOnHide());
		}
	};

	// This is to show the error messages inside the modal
	useEffect(() => {
		if (showModal) {
			dispatch(setIsModalOpen(true));
		} else {
			dispatch(setIsModalOpen(false));
		}
	}, [showModal, dispatch]);

	return (
		<Wrapper className="ViewEntityContainer">
			<Modal
				show={showModal}
				onHide={handleOnHide}
				onOkClick={onCreateUpdate}
				showButtons={configurations.showButtons}
				title={configurations.modalTitle}
				confirmBtnTitle={configurations.confirmBtnTitle}
				closeTitle={configurations.closeBtnTitle}
				closeBtnTitle={configurations.cancelBtnTitle}
				modalsize={configurations.modalSize}
				modalBodyStyle={configurations.modalBodyStyle}
				modalContainerClass={configurations.modalContainerClass}
				hideConfirmButton={configurations.hideConfirmButton}
				isOkButtonDisabled={isLoading}
				isCancelButtonDisabled={isLoading}
				showSaveAndCreateNewButton={configurations.showCreateNew}
				saveAndCreateNewBtnTitle={configurations.createNewBtnTitle}
				isAresView={isAresView}
			>
				<StyledBody className="ViewEntityContainerStyledBody">
					<section className="view-modal-body customScrollbar">
						<LoadingIndicator show={isLoading} />
						<section className="modal-body-container">
							{disableEditFeature ? (
								<CanvasBanner documentId={documentId} bannerType={canvasFormBannerTypes.shared} editAdcBanner />
							) : (
								<section className="modal-description">{configurations.modalDescription}</section>
							)}
							<section className="modal-component-renderer">
								{component?.render({...props, disableEditFeature}, componentRef, children)}
							</section>
						</section>
					</section>
				</StyledBody>
			</Modal>
		</Wrapper>
	);
});

ViewEntityContainer.displayName = 'ViewEntityContainer';

export default ViewEntityContainer;

const Wrapper = styled.section`
	display: flex;
`;

const StyledBody = styled.section`
	display: flex;
	height: 100%;
	.view-modal-body {
		display: block;
		width: 100%;
		padding: var(--px-20);
		height: 100%;
		.motif-loader-container {
			z-index: 500;
		}
		.modal-body-container {
			height: 100%;
			.modal-description {
				font-size: var(--px-14);
				font-weight: 300;
				line-height: normal;
				color: var(--neutrals-900);
				margin-bottom: var(--px-15);
				.entityNameBold {
					font-weight: bold;
				}
			}
			.modal-component-renderer {
				width: 100%;
				height: calc(100% - var(--px-50));
			}
		}
	}
`;
